/**
 * 班牌设备管理 API
 */
import { buildUrl, request } from './request'

/**
 * 获取所有班牌设备
 */
export function getDevices() {
  return request('/api/devices')
}

/**
 * 获取班牌设备统计
 */
export function getDeviceStatistics() {
  return request('/api/devices/statistics')
}

/**
 * 根据状态获取班牌设备
 * @param {number} status - 1:在线, 0:未绑定, -1:离线
 */
export function getDevicesByStatus(status) {
  return request(`/api/devices/status/${status}`)
}

/**
 * 根据 ID 获取班牌设备
 * @param {string} deviceId - 设备 ID
 */
export function getDevice(deviceId) {
  return request(`/api/devices/${deviceId}`)
}

/**
 * 创建班牌设备
 * @param {object} data - { device_id, machine_code, styles?, status?, create_by? }
 */
export function createDevice(data) {
  return request('/api/devices', {
    method: 'POST',
    body: JSON.stringify(data)
  })
}

/**
 * 更新班牌设备
 * @param {string} deviceId - 设备 ID
 * @param {object} data - { machine_code?, styles?, status?, update_by? }
 */
export function updateDevice(deviceId, data) {
  return request(`/api/devices/${deviceId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

/**
 * 切换设备运行模式
 * @param {string} deviceId - 设备 ID
 * @param {{ type_id: number }} data
 */
export function switchDeviceMode(deviceId, data) {
  return request(`/api/devices/${deviceId}/mode`, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

/**
 * 删除班牌设备
 * @param {string} deviceId - 设备 ID
 */
export function deleteDevice(deviceId) {
  return request(`/api/devices/${deviceId}`, {
    method: 'DELETE'
  })
}

/**
 * 立即重启设备
 * @param {string} deviceId - 设备 ID
 */
export function rebootDevice(deviceId) {
  return request(`/api/devices/${deviceId}/reboot`, {
    method: 'POST'
  })
}

/**
 * 立即关机设备
 * @param {string} deviceId - 设备 ID
 */
export function shutdownDevice(deviceId) {
  return request(`/api/devices/${deviceId}/shutdown`, {
    method: 'POST'
  })
}

/**
 * 下发设备软件更新
 * @param {string} deviceId - 设备 ID
 * @param {{ apk_url: string, version_code?: number, sha256?: string, install_mode?: string, blackout?: boolean, block_touch?: boolean, lock_message?: string, force_restart?: boolean, force_reboot?: boolean }} data
 */
export function updateDeviceApp(deviceId, data) {
  return request(`/api/devices/${deviceId}/app-update`, {
    method: 'POST',
    body: data
  })
}

/**
 * 上传 APK 文件
 * @param {File} file - APK 文件
 */
export function uploadApkFile(file) {
  const formData = new FormData()
  formData.append('file', file)

  return request('/api/upload-apk', {
    method: 'POST',
    body: formData
  })
}

/**
 * 设置一次性定时开关机
 * @param {string} deviceId - 设备 ID
 * @param {{ on_time: number[], off_time: number[] }} data
 */
export function setDevicePowerScheduleOnce(deviceId, data) {
  return request(`/api/devices/${deviceId}/power-schedule/once`, {
    method: 'POST',
    body: JSON.stringify(data)
  })
}

/**
 * 设置每周定时开关机
 * @param {string} deviceId - 设备 ID
 * @param {{ on_time: number[], off_time: number[], weekdays: number[] }} data
 */
export function setDevicePowerScheduleWeekly(deviceId, data) {
  return request(`/api/devices/${deviceId}/power-schedule/weekly`, {
    method: 'POST',
    body: JSON.stringify(data)
  })
}

/**
 * 清除定时开关机
 * @param {string} deviceId - 设备 ID
 */
export function clearDevicePowerSchedule(deviceId) {
  return request(`/api/devices/${deviceId}/power-schedule/clear`, {
    method: 'POST'
  })
}

/**
 * 批量重启设备
 * @param {string[]} deviceIds - 设备 ID 列表
 */
export function batchRebootDevices(deviceIds) {
  return request('/api/devices/batch/reboot', {
    method: 'POST',
    body: {
      device_ids: deviceIds
    }
  })
}

/**
 * 批量关机设备
 * @param {string[]} deviceIds - 设备 ID 列表
 */
export function batchShutdownDevices(deviceIds) {
  return request('/api/devices/batch/shutdown', {
    method: 'POST',
    body: {
      device_ids: deviceIds
    }
  })
}

/**
 * 批量设置一次性定时开关机
 * @param {{ device_ids: string[], on_time: number[], off_time: number[] }} data
 */
export function batchSetDevicePowerScheduleOnce(data) {
  return request('/api/devices/batch/power-schedule/once', {
    method: 'POST',
    body: data
  })
}

/**
 * 批量设置每周定时开关机
 * @param {{ device_ids: string[], on_time: number[], off_time: number[], weekdays: number[] }} data
 */
export function batchSetDevicePowerScheduleWeekly(data) {
  return request('/api/devices/batch/power-schedule/weekly', {
    method: 'POST',
    body: data
  })
}

/**
 * 批量清除定时开关机
 * @param {string[]} deviceIds - 设备 ID 列表
 */
export function batchClearDevicePowerSchedule(deviceIds) {
  return request('/api/devices/batch/power-schedule/clear', {
    method: 'POST',
    body: {
      device_ids: deviceIds
    }
  })
}

/**
 * 查询设备在线状态
 * @param {string} deviceId - 设备 ID
 */
export function getDeviceOnlineStatus(deviceId) {
  return request(`/api/devices/${deviceId}/status`)
}

/**
 * 查询设备命令历史
 * @param {string} deviceId - 设备 ID
 * @param {{ limit?: number, cmd_type?: string }} params
 */
export function getDeviceCommandHistory(deviceId, params = {}) {
  return request(buildUrl(`/api/devices/${deviceId}/command-history`, params))
}
